<template>
  <div class="page-wrap">

    <!-- ── Page Header ── -->
    <div class="page-header">
      <div>
        <h1 class="page-title">Invoice List</h1>
        <span class="page-subtitle">Manage and track all invoices</span>
      </div>
      <div class="page-header__right">
        <button class="btn-export" @click="exportToCSV" v-if="items && items.length" title="Export to CSV">
          <i class="fa fa-download"></i> Export CSV
        </button>
      </div>
    </div>

    <!-- ── Filters ── -->
    <div class="filter-panel">
      <div class="filter-panel__row">

        <div class="filter-field filter-field--grow">
          <label class="filter-label">Search</label>
          <div class="input-icon-wrap">
            <i class="fa fa-search input-icon"></i>
            <input
                placeholder="Search by code, order ID, customer name or ID..."
                v-model="inputFilters.q"
                class="dd-input dd-input--icon"
                v-on:keyup.enter="filter"
            />
          </div>
        </div>

        <div class="filter-field">
          <label class="filter-label">Filter by Date</label>
          <date-picker
              v-model="inputFilters.date"
              locale="en"
              format="YYYY-MM-DD"
              range
              clearable
              color="#f42f4b"
          />
        </div>

        <div class="filter-field filter-field--action">
          <label class="filter-label" style="visibility:hidden">Go</label>
          <button class="btn-filter" @click="filter">
            <i class="fa fa-search"></i> Filter
          </button>
        </div>

      </div>
    </div>

    <!-- ── Table ── -->
    <div class="table-panel">
      <div class="table-responsive-wrap">
        <table class="dd-table">
          <thead>
          <tr>
            <th>Code</th>
            <th>Date</th>
            <th>Customer</th>
            <th>Provider</th>
            <th>Payable</th>
            <th>Buyer</th>
            <th>Status</th>
            <th>Payment Method</th>
          </tr>
          </thead>
          <tbody>
          <router-link
              tag="tr"
              :to="{ name: 'invoice.show', params: { id: invoice.id } }"
              v-for="invoice in items"
              :key="invoice.id"
              class="dd-table__row"
          >
            <td>
              <span class="invoice-code">DD00{{ invoice.code }}</span>
            </td>
            <td class="date-cell">{{ formatDate(invoice.created_at) || '—' }}</td>
            <td>
              <div class="customer-info">
                <div class="customer-avatar">{{ (invoice.customer_name || '?')[0].toUpperCase() }}</div>
                <span class="customer-name">{{ invoice.customer_name || '—' }}</span>
              </div>
            </td>
            <td>
              <span class="provider-cell">{{ invoice.provider || '—' }}</span>
            </td>
            <td>
              <span class="payable-amount">{{ invoice.payable.amount }}</span>
              <span class="payable-currency"> {{ invoice.payable.currency.code }}</span>
            </td>
            <td>
                <span :class="['buyer-badge', invoice.buyer_type === 'admin' ? 'buyer-badge--admin' : 'buyer-badge--customer']">
                  <i :class="invoice.buyer_type === 'admin' ? 'fa fa-shield' : 'fa fa-user'"></i>
                  {{ invoice.buyer_type || '—' }}
                </span>
            </td>
            <td>
              <div class="badges-wrap">
                  <span
                      v-for="status in getUniqueStatuses(invoice)"
                      :key="status"
                      v-html="getStatusBadge(status)"
                  ></span>
              </div>
            </td>
            <td>
              <div class="badges-wrap">
                  <span
                      v-for="method in getUniquePaymentMethods(invoice)"
                      :key="method"
                      class="method-badge"
                  >
                    <i class="fa fa-credit-card"></i> {{ method }}
                  </span>
              </div>
            </td>
          </router-link>

          <tr v-if="items && items.length === 0">
            <td colspan="8" class="empty-state">
              <i class="fa fa-file-text"></i>
              <div>No invoices found</div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="pagination-wrap">
        <div class="pagination-info">
          Showing {{ items ? items.length : 0 }} of {{ totalItems }} invoices
        </div>
        <b-pagination
            v-model="page"
            :total-rows="totalItems"
            :per-page="perPage"
            last-number
            class="dd-pagination"
        ></b-pagination>
      </div>
    </div>

  </div>
</template>

<script>
import { config } from "../../helpers";

export default {
  data() {
    return {
      url: "",
      items: [],
      inputFilters: { q: "", date: [] },
      isFiltered: false,
      filters: {},
      page: 1,
      lastPage: 1,
      totalItems: 1,
      perPage: 1,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/invoices";
    this.loadData();
  },

  methods: {
    loadData() {
      let myData = { page: this.page };
      if (this.isFiltered) myData = { ...myData, date: this.filters.date, q: this.filters.q };
      axios.get(this.url, { params: myData }).then((r) => {
        this.items      = r.data.data;
        this.page       = r.data.meta.current_page;
        this.lastPage   = r.data.meta.last_page;
        this.totalItems = r.data.meta.total;
        this.perPage    = r.data.meta.per_page;
      });
    },

    filter() {
      this.page = 1;
      this.isFiltered = true;
      const filters = {};
      if (this.inputFilters.q) filters.q = this.inputFilters.q;
      if (this.inputFilters.date[0]) {
        filters.date = { start: this.inputFilters.date[0], end: this.inputFilters.date[1] };
      }
      this.filters = filters;
      this.loadData();
    },

    exportToCSV() {
      const myData = this.isFiltered ? { ...this.filters } : {};
      axios.get(this.url + "/export", { params: myData }).then((r) => {
        const url  = window.URL.createObjectURL(new Blob([r.data]));
        const link = document.createElement("a");
        link.href  = url;
        link.setAttribute("download", "invoices.csv");
        document.body.appendChild(link);
        link.click();
        link.remove();
      });
    },

    formatDate(date) {
      if (!date) return null;
      const d = new Date(date);
      return `${d.getFullYear()}-${d.getMonth()+1}-${d.getDate()} ${d.getHours()}:${String(d.getMinutes()).padStart(2,"0")}`;
    },

    getUniqueStatuses(invoice) {
      return [...new Set(invoice.items.map((i) => i.status).filter(Boolean))];
    },

    getUniquePaymentMethods(invoice) {
      return [...new Set(invoice.items.map((i) => i.payment_method).filter(Boolean))];
    },

    getStatusBadge(status) {
      const map = {
        paid:      ["status-badge status-badge--green", "Paid"],
        pending:   ["status-badge status-badge--amber", "Pending"],
        failed:    ["status-badge status-badge--red",   "Failed"],
        refunded:  ["status-badge status-badge--blue",  "Refunded"],
        canceled:  ["status-badge status-badge--gray",  "Canceled"],
        cancelled: ["status-badge status-badge--gray",  "Canceled"],
      };
      const key = status?.toLowerCase();
      const [cls, label] = map[key] ?? ["status-badge status-badge--gray", status];
      return `<span class="${cls}">${label}</span>`;
    },
  },

  watch: {
    page() { this.loadData(); },
  },
};
</script>

<style scoped>
.page-wrap    { display: flex; flex-direction: column; gap: 16px; }

.page-header  { display: flex; align-items: flex-end; justify-content: space-between; flex-wrap: wrap; gap: 12px; }
.page-title   { font-size: 20px; font-weight: 500; color: var(--text-1); margin: 0 0 2px; }
.page-subtitle { font-size: 12px; color: var(--text-3); }

.btn-export { height: 36px; padding: 0 14px; background: var(--bg-hover); border: 1px solid var(--border); border-radius: 8px; color: var(--text-2); font-size: 13px; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: border-color .15s, color .15s; }
.btn-export:hover { border-color: var(--accent); color: var(--accent); }

/* Filter */
.filter-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; padding: 1.1rem 1.25rem; }
.filter-panel__row { display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end; }
.filter-field         { display: flex; flex-direction: column; min-width: 160px; }
.filter-field--grow   { flex: 1; }
.filter-field--action { min-width: auto; }
.filter-label { font-size: 11px; font-weight: 500; color: var(--text-3); text-transform: uppercase; letter-spacing: .4px; margin-bottom: 5px; }

.dd-input { height: 36px; background: var(--bg-base); border: 1px solid var(--border); border-radius: 8px; color: var(--text-1); font-size: 13px; padding: 0 10px; outline: none; width: 100%; transition: border-color .15s; }
.dd-input:focus { border-color: var(--accent); }
.dd-input::placeholder { color: var(--text-3); }
.input-icon-wrap { position: relative; }
.input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); color: var(--text-3); font-size: 13px; }
.dd-input--icon { padding-left: 32px; }

.btn-filter { height: 36px; padding: 0 16px; background: var(--accent); border: none; border-radius: 8px; color: #fff; font-size: 13px; font-weight: 500; cursor: pointer; display: flex; align-items: center; gap: 6px; transition: opacity .15s; }
.btn-filter:hover { opacity: .88; }

/* Table */
.table-panel { background: var(--bg-panel); border: 1px solid var(--border); border-radius: 12px; overflow: hidden; }
.table-responsive-wrap { overflow-x: auto; }
.dd-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.dd-table thead tr { border-bottom: 1px solid var(--border); }
.dd-table th { padding: 10px 14px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .5px; color: var(--text-3); white-space: nowrap; text-align: left; }
.dd-table td { padding: 12px 14px; color: var(--text-2); border-bottom: 1px solid var(--border); vertical-align: middle; }
.dd-table__row { cursor: pointer; transition: background .12s; }
.dd-table__row:hover td { background: var(--bg-hover); }
.dd-table__row:last-child td { border-bottom: none; }

.invoice-code { font-weight: 600; color: var(--text-1); font-family: monospace; font-size: 13px; }
.date-cell    { font-size: 12px; color: var(--text-3); white-space: nowrap; }

.customer-info   { display: flex; align-items: center; gap: 9px; }
.customer-avatar { width: 28px; height: 28px; background: rgba(244,47,75,.12); color: var(--accent); border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 600; flex-shrink: 0; }
.customer-name   { font-size: 13px; font-weight: 500; color: var(--text-1); }

.provider-cell { font-size: 13px; color: var(--text-2); }

.payable-amount   { font-weight: 600; color: var(--text-1); font-size: 14px; }
.payable-currency { font-size: 12px; color: var(--text-3); }

.buyer-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; text-transform: capitalize; }
.buyer-badge--admin    { background: rgba(244,47,75,.1);   color: var(--accent); border: 1px solid rgba(244,47,75,.25); }
.buyer-badge--customer { background: var(--bg-hover);       color: var(--text-2); border: 1px solid var(--border); }

.badges-wrap { display: flex; flex-wrap: wrap; gap: 4px; }

.method-badge { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; background: rgba(129,140,248,.12); color: #818cf8; border: 1px solid rgba(129,140,248,.25); white-space: nowrap; }

.empty-state { text-align: center; padding: 3rem 1rem !important; color: var(--text-3); }
.empty-state i { font-size: 32px; display: block; margin-bottom: 8px; }

/* Pagination */
.pagination-wrap { display: flex; align-items: center; justify-content: space-between; padding: .75rem 1.25rem; border-top: 1px solid var(--border); flex-wrap: wrap; gap: 8px; }
.pagination-info { font-size: 12px; color: var(--text-3); }
.dd-pagination >>> .page-link { background: var(--bg-hover); border-color: var(--border); color: var(--text-2); font-size: 13px; }
.dd-pagination >>> .page-item.active .page-link { background: var(--accent); border-color: var(--accent); color: #fff; }
.dd-pagination >>> .page-link:hover { background: var(--bg-hover); border-color: var(--accent); color: var(--accent); }

@media (max-width: 768px) {
  .filter-panel__row { flex-direction: column; }
  .filter-field, .filter-field--grow { width: 100%; }
}
</style>